import { createMultifileUploadSlice } from "@nova/slice-multifile-upload"
import { withRoot } from "@nova/with-root"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import { ImageProcessingState, ImageWarnings } from "@nova/constants"
import { videoWarningProcessor } from "./VideoWarnings"
import { addFlightUsage } from "features/data-management/workspace/subscriptions/module"
import { get, memoize } from "nodash"
import { VideoJob } from "@nova/protobuf"
import { selectVideoFlightById } from "features/entity-caching/flights/selectors"
import { dateAsNumber } from "@nova/serializer/src/client/util"
import { VideoMetadata } from "@nova/slice-video"
import { parseTelemetryFile } from "./telemetry"
import store from "integrations/redux/store"

type VideoMeta = {
    organizationID: string
    fireID: string
    flightID: string
    setID: string
    file: File
}

export type VideoState = {
    id: string
    name: string
    s3Key?: string
    state: ImageProcessingState
    warnings?: ImageWarnings
    deletedAt?: string
    isSRT?: boolean
    takenAt?: number
    size?: number
}

export const getVideoS3Key = ({ organizationID, fireID, flightID, file }: VideoMeta) => {
    const sanitizedFileName = file.name.toLowerCase().includes(".mp4") ? file.name : "metadata.srt"
    return `public/org/${organizationID}/fire/${fireID}/flight/${flightID}/video/${sanitizedFileName}`
}

const {
    actions,
    reducer: imagesReducer,
    selectors: {
        selectSet,
        selectUpload,
        selectUploadsForSet,
        selectUploadIdsForSet,
        selectCancelledUploadIdsForSet,
        selectUploadsForSetSummary: baseSelectUploadsForSetSummary,
        selectPendingForSet: baseSelectPendingForSet,
    },
} = createMultifileUploadSlice<"video-v1", VideoState, VideoMeta>(
    "video-v1",
    (m: VideoMeta) => {
        const isSRT = !m.file.name.toLowerCase().includes(".mp4")
        const lastModifiedDate = get(m.file, "lastModifiedDate")
        return {
            id: m.file.name.toLowerCase().includes(".mp4") ? m.file.name : "metadata.srt",
            name: m.file.name,
            state: ImageProcessingState.Pending,
            size: m.file.size,
            takenAt: dateAsNumber(lastModifiedDate as string | Date | undefined), //i know this isnt actually a takenAt but it seems to be all we got
            isSRT,
        } satisfies VideoState
    },
    {
        addUploadToProject: null,
    },
    async (state, meta, update, { useSignal }) => {
        const deleteSignal = useSignal("delete", state.id)
        deleteSignal.then(() => {
            if (state.state !== ImageProcessingState.Processing)
                state = { ...state, deletedAt: new Date().toISOString() }
        })
        const s3Key = getVideoS3Key(meta)
        state = {
            ...state,
            s3Key,
            state: ImageProcessingState.ProcessingDone,
        }

        if (state.isSRT) {
            const metadata = await parseTelemetryFile(meta.file)
            if (metadata) {
                store.dispatch(setLocalVideoEvents({ flightId: meta.flightID, data: metadata }))
            }
        }

        update(state)
        return state
    },
    videoWarningProcessor,
    false
)

export const stageFiles = (
    organizationID: string,
    fireID: string,
    flightID: string,
    files: File[],
    onFinish?: () => void
) => {
    const meta = new Array<VideoMeta>(files.length)
    let totalSize = 0
    for (let i = 0; i < files.length; i++) {
        meta[i] = {
            organizationID,
            fireID,
            flightID,
            setID: flightID,
            file: files[i],
        }
        totalSize += files[i].size
    }
    addFlightUsage(flightID, totalSize)
    return actions.stageFiles({ meta, onFinish })
}

export const uploadFlight = (id: string) => actions.signal("upload", id)
export const deleteUploadSet = actions.deleteUploadSet
export const deleteUpload = actions.deleteUpload

// this is summarizing the state of uploads for a set
// it's useful for determining if a set is uploading, how much of it is uploading, etc.
const selectUploadsForSetSummary = (id: string) =>
    baseSelectUploadsForSetSummary(
        id,
        (t) => t.state,
        (t) => t.state === ImageProcessingState.UploadDone
    )

const selectPendingUploadsForSet = (id: string) => baseSelectPendingForSet(id, "upload")

export type VideoJobV1 = Omit<VideoJob, "metadataPath"> & { distance: number; duration: string }

export const selectVideoJobByFlightId = memoize((id: string) =>
    createSelector([selectVideoFlightById(id)], (flight) => flight?.job as VideoJob | VideoJobV1)
)

type UIState = {
    playing: boolean
    statusCheckerOpen: boolean
    selectedUpload: Record<string, boolean>
    localVideoEvents: Record<string, VideoMetadata>
}

const uiStateName = "video-ui-v1"
type uiStateNameType = typeof uiStateName

const {
    reducer: uiStateReducer,
    actions: { setPlaying, setStatusCheckerOpen, setSelectedUpload, setLocalVideoEvents },
} = createSlice({
    name: uiStateName,
    initialState: {
        playing: false,
        selectedUpload: {},
        statusCheckerOpen: false,
        localVideoEvents: {},
    } as UIState,
    reducers: {
        setPlaying: (state, action: { payload: boolean }) => {
            state.playing = action.payload
        },
        setStatusCheckerOpen: (state, action) => {
            state.statusCheckerOpen = action.payload
        },
        setSelectedUpload: (state, action) => {
            state.selectedUpload = action.payload
        },
        setLocalVideoEvents: (
            state,
            { payload: { flightId, data } }: { payload: { flightId: string; data: VideoMetadata } }
        ) => {
            state.localVideoEvents[flightId] = data
        },
    },
})

const { selectPlaying, selectSelectedUpload, selectStatusCheckerOpen, selectVideoEvents } =
    withRoot((r: Record<uiStateNameType, UIState>) => r[uiStateName], {
        selectPlaying: () => (s) => s.playing,
        selectStatusCheckerOpen: () => (s) => s.statusCheckerOpen,
        selectSelectedUpload: () => (s) => s.selectedUpload,
        selectVideoEvents: (flightId: string) => (s) => s.localVideoEvents[flightId],
    })

// export selectors
export {
    selectSet,
    selectUpload,
    selectUploadsForSet,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
    selectPendingUploadsForSet,
    selectUploadsForSetSummary,
    selectStatusCheckerOpen,
    selectSelectedUpload,
    selectPlaying,
    selectVideoEvents,
}

// export actions
export { setPlaying, setStatusCheckerOpen, setSelectedUpload, setLocalVideoEvents }

// export reducer
export const reducer = {
    ...imagesReducer,
}

export const uiReducer = { [uiStateName]: uiStateReducer }
